import { Close } from "@mui/icons-material";
import {
  Checkbox,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import CustomToast from "src/Components/CustomToast";
import { Recon360Context } from "../Recon360";

const SheetSelectionModal = () => {
  const {
    sheetsArrRef,
    selectedSheets,
    setSelectedSheets,
    openSheetSelectionModal,
    setOpenSheetSelectionModal,
    uploadSelectedSheets,
    setWhichActionRequired,
    setDisableUploadBothSide,
  } = useContext(Recon360Context);
  const [currentPage, setCurrentPage] = useState(0);
  const filteredSheetsArrRef = sheetsArrRef.current.filter((file) => file.sheets.length > 1);
  function isSelected(filename, sheet) {
    const selectedFile = selectedSheets.find((item) => item.filename === filename);
    return selectedFile && selectedFile.sheets.includes(sheet);
  }

  const handleCheckboxChange = (filename, sheet, selectAll = null) => {
    setSelectedSheets((prevSelectedSheets) => {
      const existingFileIndex = prevSelectedSheets.findIndex((item) => item.filename === filename);

      if (existingFileIndex !== -1) {
        // File exists, toggle sheet selection
        const updatedFile = { ...prevSelectedSheets[existingFileIndex] };

        if (selectAll === true) {
          // Select all sheets
          updatedFile.sheets = [...(filteredSheetsArrRef[currentPage]?.sheets || [])];
        } else if (selectAll === false) {
          // Deselect all sheets
          updatedFile.sheets = [];
        } else if (sheet) {
          updatedFile.sheets = updatedFile.sheets.includes(sheet)
            ? updatedFile.sheets.filter((selectedSheet) => selectedSheet !== sheet)
            : [...updatedFile.sheets, sheet];
        }

        return [
          ...prevSelectedSheets.slice(0, existingFileIndex),
          updatedFile,
          ...prevSelectedSheets.slice(existingFileIndex + 1),
        ];
      } else {
        // File does not exist, add new entry
        return [
          ...prevSelectedSheets,
          {
            filename,
            sheets: [sheet],
          },
        ];
      }
    });
  };

  const handleNextPage = () => {
    if (validateSelectedSheets()) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handleBackPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const showSelectedSheets = () => {
    if (validateSelectedSheets()) {
      uploadSelectedSheets();
    }
  };

  const validateSelectedSheets = () => {
    const missingSheets = filteredSheetsArrRef.slice(0, currentPage + 1).filter((file) => {
      const selectedFile = selectedSheets.find((item) => item.filename === file.filename);
      return !selectedFile || selectedFile.sheets.length === 0;
    });

    if (missingSheets.length > 0) {
      // alert(`Please select at least one sheet for each file: ${missingSheets.map((file) => file.filename).join(", ")}`);
      toast.error(<CustomToast message="Please select at least one sheet for each file" />);
      return false;
    }
    return true;
  };
  return (
    <div>
      <MuiDialog open={openSheetSelectionModal} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth={true}>
        <DialogTitle className="dialog-title">
          <div
            className="heading-m"
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "350px" }}
            title={filteredSheetsArrRef[currentPage]?.filename}
          >
            {filteredSheetsArrRef[currentPage]?.filename}
          </div>
          <div className="vertical_center_align">
            <span>
              {currentPage + 1}/{filteredSheetsArrRef.length}
            </span>
            <IconButton
              onClick={() => {
                setOpenSheetSelectionModal(false);
                setWhichActionRequired(null);
                setDisableUploadBothSide(false);
              }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers sx={{ display: "flex", flexFlow: "column", gap: 0.5, height: "35vh" }}>
          <div>
            <FormControl component="fieldset">
              <FormLabel component="legend" style={{ color: "black" }}>
                Please select the sheets you want to upload for the above-mentioned file.
              </FormLabel>
              <FormGroup>
                {/* Select All Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filteredSheetsArrRef[currentPage]?.sheets.every((sheet) =>
                        isSelected(filteredSheetsArrRef[currentPage]?.filename, sheet)
                      )}
                      indeterminate={
                        filteredSheetsArrRef[currentPage]?.sheets.some((sheet) =>
                          isSelected(filteredSheetsArrRef[currentPage]?.filename, sheet)
                        ) &&
                        !filteredSheetsArrRef[currentPage]?.sheets.every((sheet) =>
                          isSelected(filteredSheetsArrRef[currentPage]?.filename, sheet)
                        )
                      }
                      onChange={(e) => {
                        const selectAll = e.target.checked;
                        handleCheckboxChange(filteredSheetsArrRef[currentPage]?.filename, null, selectAll);
                      }}
                    />
                  }
                  label="Select All"
                  sx={{
                    color: "rgb(25, 118, 210)",
                    fontWeight: 700,
                    "& .MuiFormControlLabel-label": {
                      color: "rgb(25, 118, 210)",
                      fontWeight: 700,
                    },
                  }}
                />
                {/* Individual Checkboxes */}
                {filteredSheetsArrRef[currentPage]?.sheets &&
                  filteredSheetsArrRef[currentPage].sheets.map((sheet) => (
                    <FormControlLabel
                      key={sheet}
                      control={
                        <Checkbox
                          checked={isSelected(filteredSheetsArrRef[currentPage]?.filename, sheet)}
                          onChange={() => handleCheckboxChange(filteredSheetsArrRef[currentPage]?.filename, sheet)}
                        />
                      }
                      label={sheet}
                    />
                  ))}
              </FormGroup>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="text"
            disabled={currentPage === 0}
            onClick={handleBackPage}
          >
            Back
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            onClick={currentPage === filteredSheetsArrRef.length - 1 ? showSelectedSheets : handleNextPage}
          >
            {currentPage === filteredSheetsArrRef.length - 1 ? "Finish" : "Next"}
          </CustomButton>
        </DialogActions>
      </MuiDialog>
    </div>
  );
};

export default SheetSelectionModal;
